import { lazy } from 'react';

import type { RouteConfig } from '../../types';

const DashboardLayout = lazy(() => import('@/layouts/DashboardLayout'));
const ClientsLayout = lazy(() => import('@/pages/clients/layout'));
const ClientDetailLayout = lazy(() => import('@/pages/clients/detail/layout'));
const ClientsListPage = lazy(() => import('@/pages/clients'));
const ClientCreatePage = lazy(() => import('@/pages/clients/create'));
const ClientDetailIndexPage = lazy(() => import('@/pages/clients/detail'));
const ClientOverviewPage = lazy(() => import('@/pages/clients/detail/overview'));
const ClientUpdatePage = lazy(() => import('@/pages/clients/detail/update'));
const CodesListPage = lazy(() => import('@/pages/clients/detail/codes'));
const CodeCreatePage = lazy(() => import('@/pages/clients/detail/codes/create'));
const CodeUpdatePage = lazy(() => import('@/pages/clients/detail/codes/update'));
const CodeAddonsListPage = lazy(() => import('@/pages/clients/detail/codeAddons'));
const CodeAddonCreatePage = lazy(() => import('@/pages/clients/detail/codeAddons/create'));
const CodeAddonUpdatePage = lazy(() => import('@/pages/clients/detail/codeAddons/update'));
const ClientJobTypesListPage = lazy(() => import('@/pages/clients/detail/jobTypes'));
const ClientJobTypeCreatePage = lazy(() => import('@/pages/clients/detail/jobTypes/create'));
const ClientJobTypeUpdatePage = lazy(() => import('@/pages/clients/detail/jobTypes/update'));
const PhotoTypesListPage = lazy(() => import('@/pages/clients/detail/photoTypes'));
const PhotoTypeCreatePage = lazy(() => import('@/pages/clients/detail/photoTypes/create'));
const PhotoTypeUpdatePage = lazy(() => import('@/pages/clients/detail/photoTypes/update'));
const ClientQCTemplatePage = lazy(() => import('@/pages/clients/detail/qcTemplate'));
const ClientUserAssessmentTemplatePage = lazy(() => import('@/pages/clients/detail/userAssessmentTemplate'));
const ContractsListPage = lazy(() => import('@/pages/clients/detail/contracts'));
const ContractCreatePage = lazy(() => import('@/pages/clients/detail/contracts/create'));
const ContractDetailLayout = lazy(() => import('@/pages/clients/detail/contracts/detail/layout'));
const ContractDetailIndexPage = lazy(() => import('@/pages/clients/detail/contracts/detail'));
const ContractViewPage = lazy(() => import('@/pages/clients/detail/contracts/detail/view'));
const ContractUpdatePage = lazy(() => import('@/pages/clients/detail/contracts/detail/update'));
const ContractHistoryPage = lazy(() => import('@/pages/clients/detail/contracts/detail/history'));
const ContractJobSpecificationsListPage = lazy(
  () => import('@/pages/clients/detail/contracts/detail/jobSpecifications')
);
const JobSpecificationCreatePage = lazy(
  () => import('@/pages/clients/detail/contracts/detail/jobSpecifications/create')
);
const JobSpecificationUpdatePage = lazy(
  () => import('@/pages/clients/detail/contracts/detail/jobSpecifications/update')
);
const JobSpecificationViewPage = lazy(() => import('@/pages/clients/detail/contracts/detail/jobSpecifications/view'));
const ClientJobSpecificationsListPage = lazy(() => import('@/pages/clients/detail/jobSpecifications'));

/**
 * UG-4501 — client entity routes (legacy `app/(dashboard)/clients/*`).
 * `ClientsLayout` mounts the section breadcrumb; `ClientDetailLayout` is the
 * legacy `[clientId]/layout.tsx` shell (client fetch + header + sub-nav)
 * around every nested resource. Permissions: `@/config/auth/routeAccess`
 * (ported 1:1 from the legacy table).
 */
export const clientRoutes: RouteConfig = {
  path: '/clients',
  layout: DashboardLayout,
  children: [
    {
      path: '',
      layout: ClientsLayout,
      children: [
        { path: '', element: ClientsListPage },
        { path: '/create', element: ClientCreatePage },
        {
          path: '/:clientId',
          layout: ClientDetailLayout,
          children: [
            { path: '', element: ClientDetailIndexPage },
            { path: '/overview', element: ClientOverviewPage },
            { path: '/update', element: ClientUpdatePage },

            { path: '/codes', element: CodesListPage },
            { path: '/codes/create', element: CodeCreatePage },
            { path: '/codes/:codeId/update', element: CodeUpdatePage },
            { path: '/codeAddons', element: CodeAddonsListPage },
            { path: '/codeAddons/create', element: CodeAddonCreatePage },
            { path: '/codeAddons/:codeAddonId/update', element: CodeAddonUpdatePage },

            { path: '/jobTypes', element: ClientJobTypesListPage },
            { path: '/jobTypes/create', element: ClientJobTypeCreatePage },
            { path: '/jobTypes/:jobTypeId/update', element: ClientJobTypeUpdatePage },
            { path: '/photoTypes', element: PhotoTypesListPage },
            { path: '/photoTypes/create', element: PhotoTypeCreatePage },
            { path: '/photoTypes/:photoTypeId/update', element: PhotoTypeUpdatePage },

            { path: '/contracts', element: ContractsListPage },
            { path: '/contracts/create', element: ContractCreatePage },
            {
              path: '/contracts/:contractId',
              layout: ContractDetailLayout,
              children: [
                { path: '', element: ContractDetailIndexPage },
                { path: '/view', element: ContractViewPage },
                { path: '/update', element: ContractUpdatePage },
                { path: '/history', element: ContractHistoryPage },
                { path: '/jobSpecifications', element: ContractJobSpecificationsListPage },
                { path: '/jobSpecifications/create', element: JobSpecificationCreatePage },
                { path: '/jobSpecifications/:jobSpecificationId/view', element: JobSpecificationViewPage },
                { path: '/jobSpecifications/:jobSpecificationId/update', element: JobSpecificationUpdatePage },
              ],
            },
            { path: '/jobSpecifications', element: ClientJobSpecificationsListPage },

            { path: '/qcTemplate', element: ClientQCTemplatePage },
            { path: '/userAssessmentTemplate', element: ClientUserAssessmentTemplatePage },
          ],
        },
      ],
    },
  ],
};
